import { EntityDataAttribute } from "types/Models/entity";
import { BillingInvoice } from "types/Models/billing";
import { emptyApi } from "./emptyApi";
import { Cart } from "./products";
import {
  RegistrationActionType,
  RegistrationStatus,
  StateFilingData,
} from "./registrations";

export type EntityDataAttributeWithFieldKey = EntityDataAttribute & {
  field_key: string;
  order: number;
};

export type StateRegistrationType = {
  data_attributes: EntityDataAttributeWithFieldKey[];
  status: RegistrationStatus;
  status_text: string;
  title: string;
  purpose: string;
  relevance: string;
  action_type: RegistrationActionType;
  action_label: string;
  action_description: string;
  short_description: string;
  filing_data: StateFilingData;
  department_of_registration: string;
  state_id: string;
  state: string;
  cart_status: string | null;
  task_uuid: string | null;
  is_inkle_ra?: boolean;
};

export const StateRegistrationName = {
  PAY_ROLL: "PAY_ROLL",
  REGISTERED_AGENT: "REGISTERED_AGENT",
  SECRETARY_OF_STATE: "SECRETARY_OF_STATE",
} as const;

export type StateRegistrationNameType =
  (typeof StateRegistrationName)[keyof typeof StateRegistrationName];

export type StateComplianceStatus = {
  statuses: StateRegistrationStatusFilterType[];
  missing_field_labels: string[];
  is_sos_done: boolean;
  is_payroll_done: boolean;
};

export type StateRegistrationsRes = {
  state: string;
  is_home_state: boolean;
  state_id: string;
  PAY_ROLL: StateRegistrationType;
  REGISTERED_AGENT: StateRegistrationType;
  SECRETARY_OF_STATE: StateRegistrationType;
  inkle_ra_active: boolean;
  inkle_ra_expiration_date: string | null;
  external_ra_expiration_date: string | null;
  sos_required_for_payroll: boolean;
  has_sos_intake: boolean;
  has_payroll_intake: boolean;
  has_sos_task_launched: boolean;
  has_payroll_task_launched: boolean;
  is_self_reported: boolean;
  withdrawal_date: string | null;
  compliance_status: StateComplianceStatus;
  deadline_task_uuid: string | null;
  deadline_date: string | null;
  is_deadline_overdue: boolean;
  is_deadline_due: boolean;
};

export type StateRegistrationSuggestion = {
  state: string;
  state_id: string;
  suggestion_type: "in_cart" | "foreign_qualification_in_cart";
};

export type StateRegistrationsSummary = {
  total_states: number;
  not_registered_count: number;
  filing_in_progress_count: number;
  missing_info_count: number;
  registered_count: number;
  incorporated_count: number;
  overdue_filings_count: number;
  upcoming_filings_count: number;
};

export const StateRegistrationStatusFilter = {
  NOT_REGISTERED: "not_registered",
  FILING_IN_PROGRESS: "filing_in_progress",
  MISSING_INFO: "missing_info",
  REGISTERED: "registered",
  INCORPORATED: "incorporated",
} as const;

export type StateRegistrationStatusFilterType =
  (typeof StateRegistrationStatusFilter)[keyof typeof StateRegistrationStatusFilter];

export const FilingDeadlineFilter = {
  OVERDUE: "overdue",
  DUE: "due",
} as const;

export type FilingDeadlineFilterType =
  (typeof FilingDeadlineFilter)[keyof typeof FilingDeadlineFilter];

export const stateRegistrationsApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getStateRegistrations: build.query<
      StateRegistrationsRes[],
      {
        groupId: string;
        entityId: string;
        searchTerm?: string;
        statuses?: StateRegistrationStatusFilterType[];
        deadlineFilters?: FilingDeadlineFilterType[];
      }
    >({
      query: ({
        groupId,
        entityId,
        searchTerm,
        statuses,
        deadlineFilters,
      }) => ({
        url: `/api/inkle/tax_compliance/group/${groupId}/entity/${entityId}/state_registrations/`,
        params: {
          ...(searchTerm && { search_term: searchTerm }),
          ...(statuses?.length && { status: statuses.join(",") }),
          ...(deadlineFilters?.length && {
            deadline: deadlineFilters.join(","),
          }),
        },
      }),
      providesTags: ["STATE_REGISTRATIONS"],
    }),

    getStateRegistrationById: build.query<
      StateRegistrationType,
      {
        groupId: string;
        entityId: string;
        stateId: string;
        stateType: StateRegistrationNameType;
      }
    >({
      query: ({ groupId, entityId, stateId, stateType }) =>
        `/api/inkle/tax_compliance/group/${groupId}/entity/${entityId}/state_registration/${stateId}/?type=${stateType}`,
      providesTags: ["STATE_REGISTRATIONS"],
    }),

    updateStateRegistration: build.mutation<
      StateRegistrationType,
      {
        payload: {
          data_attributes: EntityDataAttributeWithFieldKey[];
          type: StateRegistrationNameType;
          status?: RegistrationStatus;
        };
        groupId: string;
        entityId: string;
        stateId: string;
      }
    >({
      query: ({ groupId, payload, entityId, stateId }) => ({
        url: `/api/inkle/tax_compliance/group/${groupId}/entity/${entityId}/state_registration/${stateId}/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["STATE_REGISTRATIONS"],
    }),

    addStateRegistration: build.mutation<
      StateRegistrationType,
      {
        groupId: string;
        entityId: string;
        stateId: string;
        isSelfReported?: boolean;
      }
    >({
      query: ({ groupId, entityId, stateId, isSelfReported }) => ({
        url: `/api/inkle/tax_compliance/group/${groupId}/entity/${entityId}/state_registration/${stateId}/`,
        method: "POST",
        body: { is_self_reported: Boolean(isSelfReported) },
      }),
      invalidatesTags: ["STATE_REGISTRATIONS"],
    }),

    getStateRegistrationSuggestions: build.query<
      StateRegistrationSuggestion[],
      { groupId: string; entityId: string }
    >({
      query: ({ groupId, entityId }) =>
        `/api/inkle/tax_compliance/group/${groupId}/entity/${entityId}/state_registration_suggestions/`,
      providesTags: ["STATE_REGISTRATION_SUGGESTIONS"],
    }),

    getStateRegistrationsSummary: build.query<
      StateRegistrationsSummary,
      { groupId: string; entityId: string }
    >({
      query: ({ groupId, entityId }) =>
        `/api/inkle/tax_compliance/group/${groupId}/entity/${entityId}/state_registrations_summary/`,
      providesTags: ["STATE_REGISTRATIONS"],
    }),

    submitRegistrationIntake: build.mutation<
      {
        submitted: number;
        invoice: BillingInvoice | null;
        cart_details: Cart;
        request_id: string;
      },
      {
        groupId: string;
        entityId: string;
        stateId: string;
        intakeEntries: {
          type: StateRegistrationNameType;
          fields: Record<string, string>;
        }[];
      }
    >({
      query: ({ groupId, entityId, stateId, intakeEntries }) => ({
        url: `/api/inkle/tax_compliance/group/${groupId}/entity/${entityId}/state_registration/${stateId}/intake/`,
        method: "POST",
        body: { intake_entries: intakeEntries },
      }),
      invalidatesTags: ["CART", "STATE_REGISTRATIONS"],
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetStateRegistrationsQuery,
  useGetStateRegistrationByIdQuery,
  useGetStateRegistrationSuggestionsQuery,
  useGetStateRegistrationsSummaryQuery,
  useUpdateStateRegistrationMutation,
  useAddStateRegistrationMutation,
  useSubmitRegistrationIntakeMutation,
} = stateRegistrationsApis;
